// Recuperação de 2FA perdida, passo 2 (`spec/17-frontend.md` §8.1, AUTH-05). Pública,
// mesmo layout de `reset-password`: quem chega aqui está deslogado, com o token do email.
//
// O confirm não é disparado sozinho no `useEffect`: um prefetch de link em cliente de
// email queimaria o token de uso único sem o dono ter clicado em nada.

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { confirmTwoFactorRecovery } from '../api/auth.ts';
import { errorMessageOf } from '../api/errors.ts';
import { AuthShell, FormError } from '../components/app/auth-shell.tsx';
import { Button } from '../components/ui/button.tsx';

const searchSchema = z.object({ token: z.string().optional() });

function Recover2fa() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [error, setError] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  if (token === undefined || token === '') {
    return (
      <AuthShell title="Link inválido" description="Peça um link novo na tela de login.">
        <Button asChild variant="outline" className="w-full">
          <Link to="/login">Voltar para o login</Link>
        </Button>
      </AuthShell>
    );
  }

  const confirm = async (): Promise<void> => {
    setError(undefined);
    setSubmitting(true);
    try {
      await confirmTwoFactorRecovery(token);
      await navigate({ to: '/login', search: { recovered: true } });
    } catch (caught) {
      setError(errorMessageOf(caught));
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Desligar a verificação em duas etapas"
      description="Você volta a entrar só com email e senha."
    >
      <div className="flex flex-col gap-4">
        <ul className="flex list-disc flex-col gap-1.5 pl-4 text-11 text-muted">
          <li>Todas as sessões abertas são encerradas.</li>
          <li>Os backup codes antigos deixam de valer.</li>
          <li>Os saques ficam bloqueados por 24 horas.</li>
        </ul>
        <FormError message={error} />
        <Button
          type="button"
          className="h-11"
          disabled={submitting}
          onClick={() => {
            void confirm();
          }}
        >
          {submitting ? 'Desligando…' : 'Desligar e voltar ao login'}
        </Button>
        <Button asChild variant="ghost" className="h-11">
          <Link to="/login">Cancelar</Link>
        </Button>
      </div>
    </AuthShell>
  );
}

export const Route = createFileRoute('/recover-2fa/')({
  validateSearch: searchSchema,
  component: Recover2fa,
});
